import { Item } from '../components/item-v2/item-types';
import { joinString, separator } from '../shared/utils';
import { containersApi } from './api';
import { ItemAPI } from './item';
import { ServiceProviderApi } from './serviceProvider';

export const optionTypes = {
  shippingRoute: 'shipping-route',
  freightCode: 'freight-code',
  supplier: 'supplier',
  supplierContact: 'supplier-contact',
  supplierAddress: 'supplier-address',
  carrier: 'carrier',
  unitOfMeasure: 'uom',
  customerAddress: 'customer-address',
  item: 'item',
  itemShippingSpec: 'item-shipping-spec',
  itemTaxCode: 'item-tax-code',
  predefinedRoute: 'predefined-route',
  predefinedRouteProcess: 'process',
  opportunityType: 'opportunity-type',
  packageType: 'packageType',
  country: 'country',
  customer: 'customer',
  customerReorderItem: 'customer-reorder-item',
  companyContact: 'company-contact',
  invoiceDelivery: 'invoice-delivery-type',
  opportunityStatus: 'opportunity-status',
  lostSalesOptions: 'lost-sales-options',
  leadSource: 'lead-source',
  paymentTerm: 'payment-term',
  fieldValueSuggestion: 'field-value-suggestion',
  companyInfo: 'company-info',
  customerContact: 'customer-contact',
  customerPartNumber: 'customer-part-number',
  priority: 'priority',
  broker: 'broker',
  importer: 'importer',
  priceLibraries: 'price-libraries',
  noteClass: 'note-class',
  noteAreas: 'note-area',
  vendorAreas: 'vendor-area',
  supplierAreas: 'supplier-area',
  contactRoles: 'contact-roles',
  industry: 'industry',
  customerType: 'customer-type',
  leadSourceForCustomers: 'lead-source-for-customers',
  customerFlags: 'customer-flags',
  fileLinkGroup: 'file-link-group',
  contactBand: 'contact-band',
  internationalStatus: 'international-status',
} as const;

export type OptionType = typeof optionTypes[keyof typeof optionTypes];

export interface BaseOption {
  type?: OptionType | string;
  value?: string | number;
  label?: string;
  [key: string]: unknown;
}

export const priorityValues = [1, 2, 3, 4, 5] as const;

export const OptionsAPI = {
  getOptionsByType: (
    type: OptionType | string,
    query: Record<string, unknown> = {}
  ) => {
    const queries = Object.keys(query)
      .filter(
        (k) => query[k] !== undefined && query[k] !== null && query[k] !== ''
      )
      .map(
        (k) =>
          `${encodeURIComponent(k)}=${encodeURIComponent(String(query[k]))}`
      )
      .join('&');

    return containersApi.get(`options/${type}?${queries}`);
  },
  getPublicOptions: (type: OptionType | string) =>
    containersApi.get(`options/public/${type}`),
};

export const sortOptions = <T extends BaseOption>(
  options: T[] | null | undefined,
  sortByKey: keyof T = 'label' as keyof T,
  descending = false
): T[] => {
  if (options && options.length > 0) {
    const sorted = options.sort((a, b) => {
      const aVal = a[sortByKey];
      const bVal = b[sortByKey];
      const sort = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return descending ? sort * -1 : sort;
    });
    return sorted;
  }
  return options || [];
};

interface FindOptionParams {
  options?: BaseOption[] | null;
  type: OptionType | string;
  value: string | number;
}

export const findOption = ({ options, type, value }: FindOptionParams) => {
  return (options || []).find((o) => o.type === type && o.value === value);
};

export const getContactRoleOptions = async () => {
  const { data: contactRoles } = await OptionsAPI.getOptionsByType(
    optionTypes.contactRoles
  );
  contactRoles?.forEach(
    (
      cr: BaseOption & { contact_role_id?: string; contact_role_desc?: string }
    ) => {
      cr.type = optionTypes.contactRoles;
      cr.value = cr.contact_role_id;
      cr.label = cr.contact_role_desc;
    }
  );

  return sortOptions(contactRoles);
};

export const getNoteClassOptions = async () => {
  const { data: noteClasses } = await OptionsAPI.getOptionsByType(
    optionTypes.noteClass
  );

  noteClasses?.forEach(
    (
      nc: BaseOption & {
        notepad_class_id?: string;
        notepad_class_desc?: string;
      }
    ) => {
      nc.type = optionTypes.noteClass;
      nc.value = nc.notepad_class_id;
      nc.label = nc.notepad_class_desc;
    }
  );
  return sortOptions(noteClasses);
};

export const getSupplierListOptions = (
  supplierList:
    | (BaseOption & { supplier_id?: string; supplier_name?: string })[]
    | null
    | undefined
) => {
  supplierList?.forEach((va) => {
    va.type = optionTypes.supplier;
    va.value = va.supplier_id;
    va.label = joinString([va.supplier_name, `ID: ${va.supplier_id}`]);
  });
  return sortOptions(supplierList);
};

export const getVendorAreaOptions = async () => {
  const { data: vendorAreas } = await OptionsAPI.getOptionsByType(
    optionTypes.vendorAreas
  );

  vendorAreas?.forEach((va: BaseOption & { area?: string }) => {
    va.type = optionTypes.vendorAreas;
    va.value = va.area;
    va.label = va.area;
  });
  return sortOptions(vendorAreas);
};

export const getSupplierAreaOptions = async () => {
  const { data: supplierAreas } = await OptionsAPI.getOptionsByType(
    optionTypes.supplierAreas
  );

  supplierAreas?.forEach((va: BaseOption & { area?: string }) => {
    va.type = optionTypes.supplierAreas;
    va.value = va.area;
    va.label = va.area;
  });
  return sortOptions(supplierAreas);
};

export const getNoteAreaOptions = async () => {
  const { data: noteAreas } = await OptionsAPI.getOptionsByType(
    optionTypes.noteAreas
  );

  noteAreas?.forEach((na: BaseOption & { area?: string }) => {
    na.type = optionTypes.noteAreas;
    na.value = na.area;
    na.label = na.area;
  });
  return sortOptions(noteAreas);
};

interface BrokerData extends BaseOption {
  id?: number;
  address_name?: string;
  phys_address1?: string;
  phys_city?: string;
  phys_state?: string;
  phys_postal_code?: string;
  phys_country?: string;
  direct_phone?: string;
  email_address?: string;
}

export const getBrokerOptions = async () => {
  const { data: brokers } = await OptionsAPI.getOptionsByType(
    optionTypes.broker
  );
  brokers?.forEach((cb: BrokerData) => {
    cb.type = optionTypes.broker;
    cb.value = `${cb.id}`;
    cb.label = joinString([
      cb.address_name,
      joinString(
        [
          cb.phys_address1,
          cb.phys_city,
          cb.phys_state,
          cb.phys_postal_code,
          cb.phys_country,
        ],
        ', '
      ),
      cb.direct_phone,
      cb.email_address,
    ]);
  });
  return sortOptions(brokers);
};

interface ImporterParams {
  customerId: string | number;
}

interface ImporterData extends BaseOption {
  contact_id?: number;
  address_name?: string;
  phys_address1?: string;
  phys_city?: string;
  phys_state?: string;
  phys_postal_code?: string;
  phys_country?: string;
  direct_phone?: string;
  email_address?: string;
}

export const getImporterOptions = async ({ customerId }: ImporterParams) => {
  const { data: importers } = await OptionsAPI.getOptionsByType(
    optionTypes.importer,
    {
      customerId,
    }
  );
  importers?.forEach((importer: ImporterData) => {
    importer.type = optionTypes.importer;
    importer.value = `${importer.contact_id}`;
    importer.label = joinString([
      importer.address_name,
      joinString(
        [
          importer.phys_address1,
          importer.phys_city,
          importer.phys_state,
          importer.phys_postal_code,
          importer.phys_country,
        ],
        ', '
      ),
      importer.direct_phone,
      importer.email_address,
    ]);
  });

  return sortOptions(importers);
};

interface SupplierData extends BaseOption {
  supplier_id?: string | number;
  supplier_label?: string;
  supplier_type?: string;
  isInternal?: boolean;
  phys_city?: string;
  phys_state?: string;
  phys_postal_code?: string;
  phys_address1?: string;
  phys_address2?: string;
  phys_country?: string;
}

export const getSupplierOptions = async () => {
  const { data: suppliers } = await OptionsAPI.getOptionsByType(
    optionTypes.supplier
  );

  suppliers?.forEach((s: SupplierData) => {
    s.type = optionTypes.supplier;
    s.value = `${s.supplier_id}`;
    s.label = joinString([
      s.supplier_label,
      s.isInternal &&
        `(${s.supplier_type || 'internal supplier'}, C&P Location: ${
          s.supplier_id
        })`,
      joinString([s.phys_city, s.phys_state, s.phys_postal_code], ', '),
      `(Supplier ID: ${s.supplier_id})`,
    ]);
  });

  return sortOptions(suppliers);
};

interface SupplierContactParams {
  supplierId: string | number;
}

interface SupplierContactData extends BaseOption {
  contact_id?: number;
  first_name?: string;
  last_name?: string;
  email_address?: string;
  direct_phone?: string;
}

export const getSupplierContactOptions = async ({
  supplierId,
}: SupplierContactParams) => {
  const { data: supplierContacts } = await OptionsAPI.getOptionsByType(
    optionTypes.supplierContact,
    {
      supplierId,
    }
  );

  if (!supplierContacts) {
    return [];
  }

  supplierContacts?.forEach((s: SupplierContactData) => {
    s.type = optionTypes.supplierContact;
    s.value = `${s.contact_id}`;
    s.label = joinString([
      joinString([s.first_name, s.last_name], ' '),
      s.email_address,
      s.direct_phone,
      `(Contact ID: ${s.contact_id})`,
    ]);
  });

  return sortOptions(supplierContacts);
};

export const getSupplierAddressOptions = async () => {
  const { data: suppliers } = await OptionsAPI.getOptionsByType(
    optionTypes.supplier
  );

  suppliers?.forEach((s: SupplierData) => {
    s.type = optionTypes.supplierAddress;
    s.value = `${s.supplier_id}`;
    s.label = joinString([
      s.supplier_label,
      s.isInternal && `(C&P Location: ${s.supplier_id})`,
      s.phys_address1,
      s.phys_address2,
      joinString(
        [s.phys_city, s.phys_state, s.phys_postal_code, s.phys_country],
        ', '
      ),
    ]);
  });

  return sortOptions(suppliers);
};

interface CarrierData extends BaseOption {
  name?: string;
  carrier_type?: string;
}

export const getCarrierOptions = async () => {
  const { data: carriers } = await OptionsAPI.getOptionsByType(
    optionTypes.carrier
  );
  carriers?.forEach((c: CarrierData) => {
    c.type = optionTypes.carrier;
    c.value = `${c.name}`;
    c.label = `${c.name}${
      c.name?.includes(c.carrier_type || '')
        ? ''
        : `${separator}${c.carrier_type}`
    }`.trim();
  });

  return sortOptions(carriers);
};

export const getPriorityOptions = () => {
  const priorityOptions = priorityValues?.map((p) => {
    return {
      type: optionTypes.priority,
      value: p,
      label: `${p}`,
    };
  });
  return sortOptions(priorityOptions as unknown as BaseOption[]);
};

interface ShippingRouteData extends BaseOption {
  route_code?: string;
  route_description?: string;
}

export const getShippingRouteOptions = async () => {
  const { data: shippingRoutes } = await OptionsAPI.getOptionsByType(
    optionTypes.shippingRoute
  );
  shippingRoutes?.forEach((sr: ShippingRouteData) => {
    sr.type = optionTypes.shippingRoute;
    sr.value = `${sr.route_code}`;
    sr.label = `${sr.route_code}${separator}${sr.route_description}`.trim();
  });

  return sortOptions(shippingRoutes);
};

interface FreightCodeData extends BaseOption {
  freight_cd?: string;
  freight_desc?: string;
}

export const getFreightCodeOptions = async () => {
  const { data } = await OptionsAPI.getOptionsByType(optionTypes.freightCode);
  data?.forEach((sr: FreightCodeData) => {
    sr.type = optionTypes.freightCode;
    sr.value = sr.freight_cd;
    sr.label = joinString([sr.freight_cd, sr.freight_desc]);
  });

  return sortOptions(data);
};

interface UnitOfMeasureData extends BaseOption {
  unit_id?: string;
  unit_description?: string;
}

export const getUnitOfMeasureOptions = async () => {
  const { data: uoms } = await OptionsAPI.getOptionsByType(
    optionTypes.unitOfMeasure
  );

  if (uoms) {
    uoms.forEach((c: UnitOfMeasureData) => {
      c.type = optionTypes.unitOfMeasure;
      c.value = c.unit_id;
      c.label = c.unit_description;
    });

    return sortOptions(uoms);
  }

  return [];
};

interface CustomerAddressData extends BaseOption {
  id?: number;
  name?: string;
  is_default_billing?: boolean;
  is_default_shipping?: boolean;
  phys_address1?: string;
  phys_address2?: string;
  phys_city?: string;
  phys_state?: string;
  phys_postal_code?: string;
  phys_country?: string;
}

export const getCustomerAddressOptions = async (
  customerId: string | number
) => {
  const { data: customerAddresses } = await OptionsAPI.getOptionsByType(
    optionTypes.customerAddress,
    { customerId }
  );

  customerAddresses?.forEach((ca: CustomerAddressData) => {
    ca.type = optionTypes.customerAddress;
    ca.value = `${ca.id}`;

    const defaultDescription =
      (ca.is_default_billing &&
        ca.is_default_shipping &&
        '(*Default billing & shipping)') ||
      (ca.is_default_billing && '(*Default billing)') ||
      (ca.is_default_shipping && '(*Default shipping)') ||
      '';

    ca.label = joinString([
      defaultDescription,
      ca.name + ` (ID: ${ca.id})`,
      ca.phys_address1,
      ca.phys_address2,
      joinString(
        [ca.phys_city, ca.phys_state, ca.phys_postal_code, ca.phys_country],
        ', '
      ),
    ]);
  });

  return sortOptions(customerAddresses);
};

export const findItemOption = (
  options: BaseOption[],
  itemId: string | number
) =>
  options.filter(
    (o) =>
      o.type == optionTypes.item &&
      (o as { item_id?: string | number }).item_id === itemId
  )[0];

interface GetItemOptionsParams {
  itemFilter?: (item: ItemData) => boolean;
  itemId?: string | number;
  itemIds?: (string | number)[];
  searchString?: string;
  searchByItemId?: boolean;
  onlyStockableItems?: boolean;
}

interface ItemData extends BaseOption, Item {
  process_description?: string;
}

export const getItemOptions = async ({
  itemFilter,
  itemId,
  itemIds,
  searchString,
  searchByItemId,
  onlyStockableItems = false,
}: GetItemOptionsParams = {}): Promise<Item[]> => {
  let { data } = await ItemAPI.lookupItems({
    itemIds: itemIds || (itemId && [itemId]) || [],
    searchString: searchString || '',
    searchByItemId,
  });
  let items = data as ItemData[];

  if (items) {
    items.forEach((i) => {
      const description =
        i.process_description || i.extended_desc || i.item_desc;
      i.type = optionTypes.item;
      i.value = i.item_id;
      i.label = `${i.item_id}${(description && `: ${description}`) || ''}`;
    });

    if (itemFilter) {
      items = items.filter(itemFilter);
    }
  }

  if (onlyStockableItems) {
    items = items.filter((item) => !item.is_ds_only);
  }

  // Use default back-end sorting
  if (searchString) {
    return items;
  }

  return sortOptions(items);
};

interface CustomerPartNumberData extends BaseOption {
  customer_part_number?: string;
}

export const getCustomerPartNumbers = async (
  customerId: string | number,
  itemId: string | number
) => {
  const { data: customerPartNumbers } = await ItemAPI.getCustomerPartNumbers(
    customerId,
    itemId
  );
  customerPartNumbers?.forEach((c: CustomerPartNumberData) => {
    c.type = optionTypes.customerPartNumber;
    c.value = c.customer_part_number;

    c.label = c.customer_part_number;
  });
  return sortOptions(customerPartNumbers);
};

interface GetItemShippingSpecsParams {
  itemId?: string | number;
}

export const getItemShippingSpecs = async ({
  itemId,
}: GetItemShippingSpecsParams = {}) => {
  const { data } = await OptionsAPI.getOptionsByType(
    optionTypes.itemShippingSpec,
    {
      itemId,
    }
  );

  return data;
};

export const getItemTaxCodes = async () => {
  const { data } = await OptionsAPI.getOptionsByType(optionTypes.itemTaxCode);

  return data;
};

interface ProcessData extends BaseOption {
  stage_code?: string;
  cost_type?: string;
  comment_process?: string;
}

export const getProcessesOptions = async () => {
  const { data: processes } = await OptionsAPI.getOptionsByType(
    optionTypes.predefinedRouteProcess
  );

  processes?.forEach((p: ProcessData) => {
    p.type = optionTypes.predefinedRouteProcess;
    p.value = p.stage_code;
    p.label = `${p.stage_code}${
      (!p.cost_type && p.comment_process !== 'Y' && ' (Missing Cost Type)') ||
      ''
    }`;
  });

  return sortOptions(processes || []);
};

interface PredefinedRouteData extends BaseOption {
  process_name?: string;
  supplier_name?: string;
  raw_item_id?: string;
  process_code?: string;
}

export const getPredefinedRouteOptions = async (
  query?: Record<string, unknown>
) => {
  const { data: predefinedRoutes } = await OptionsAPI.getOptionsByType(
    optionTypes.predefinedRoute,
    query
  );

  if (predefinedRoutes) {
    predefinedRoutes.forEach((i: PredefinedRouteData) => {
      const label = `${i.process_name}${separator}${i.supplier_name}${separator}${i.raw_item_id}`;
      i.type = optionTypes.predefinedRoute;
      i.value = i.process_code;
      i.label = label;
    });
  }

  return predefinedRoutes;
};

interface CountryData extends BaseOption {
  two_letter_code?: string;
  country_name?: string;
}

export const getCountryOptions = async () => {
  const { data: countries } = await OptionsAPI.getOptionsByType(
    optionTypes.country
  );
  countries?.forEach((c: CountryData) => {
    c.type = optionTypes.country;
    c.value = c.two_letter_code;
    c.label = `${c.country_name} (${c.two_letter_code})`;
  });

  const sortedOptions = sortOptions(countries);

  return [
    ...sortedOptions.filter((s) => ['US', 'CN'].includes(s.value as string)),
    ...sortedOptions.filter((s) => !['US', 'CN'].includes(s.value as string)),
  ];
};

interface PackageTypeData extends BaseOption {
  package_type_uid?: string;
  package_type_id?: string;
}

export const getPackageTypeOptions = async () => {
  const { data: packageTypes } = await OptionsAPI.getOptionsByType(
    optionTypes.packageType
  );
  packageTypes?.forEach((c: PackageTypeData) => {
    c.type = optionTypes.packageType;
    c.value = c.package_type_uid;
    c.label = c.package_type_id;
  });

  return sortOptions(packageTypes);
};

interface GetCustomerOptionsParams {
  customerId?: string | number;
  customerName?: string;
}

interface CustomerData extends BaseOption {
  customer_id?: string | number;
  customer_name?: string;
}

export const getCustomerOptions = async ({
  customerId,
  customerName,
}: GetCustomerOptionsParams) => {
  const { data: customers } = await OptionsAPI.getOptionsByType(
    optionTypes.customer,
    {
      customerId,
      customerName,
    }
  );

  customers?.forEach((c: CustomerData) => {
    c.type = optionTypes.customer;
    c.value = c.customer_id;
    c.label = `${c.customer_name} (${c.customer_id})`;
  });

  return sortOptions(customers);
};

interface GetCustomerContactOptionsParams {
  customerId: string | number;
}

interface CustomerContactData extends BaseOption {
  contact_id?: number;
  first_name?: string;
  last_name?: string;
  email_address?: string;
  direct_phone?: string;
}

export const getCustomerContactOptions = async ({
  customerId,
}: GetCustomerContactOptionsParams) => {
  const { data } = await OptionsAPI.getOptionsByType(
    optionTypes.customerContact,
    {
      customerId,
    }
  );

  data?.forEach((c: CustomerContactData) => {
    c.type = optionTypes.customerContact;
    c.value = c.contact_id;

    c.label = joinString([
      joinString([c.first_name, c.last_name, `(${c.contact_id})`], ' '),
      c.email_address,
      c.direct_phone,
    ]);
  });

  return sortOptions(data);
};

interface OpportunityTypeData extends BaseOption {
  data_value?: string;
  display_value?: string;
}

export const getOpportunityTypes = async (query?: Record<string, unknown>) => {
  const { data: opportunityTypes } = await OptionsAPI.getOptionsByType(
    optionTypes.opportunityType,
    query
  );

  opportunityTypes?.forEach((c: OpportunityTypeData) => {
    c.type = optionTypes.opportunityType;
    c.value = c.data_value;
    c.label = c.display_value;
  });

  return opportunityTypes;
};

export const getCustomerReorderItems = async (
  query?: Record<string, unknown>
) => {
  const { data } = await OptionsAPI.getOptionsByType(
    optionTypes.customerReorderItem,
    query
  );
  return data || [];
};

interface FilterOptionsParams {
  options?: BaseOption[] | null;
  optionType?: OptionType | string;
  optionFilter?: (option: BaseOption) => boolean;
}

export const filterOptions = (params: FilterOptionsParams = {}) => {
  const { options, optionType, optionFilter } = params;

  if (options && options.length > 0) {
    return (
      options
        .filter(
          (o) =>
            (!optionType || o.type === optionType) &&
            (!optionFilter || optionFilter(o))
        )
        ?.map((o) => {
          return {
            ...o,
            value: o.value,
            label: o.label,
          };
        }) || []
    );
  }

  return [];
};

interface GetDefaultCarrierOrBrokerOptionsParams {
  searchString?: string;
  showDeleted?: boolean;
}

interface ServiceProviderData {
  serviceProviderDescription?: string;
  serviceProviderType?: string;
  deleted?: boolean;
  serviceProviderId?: string | number;
}

interface P21ServiceLevel {
  route_description?: string;
}

// Using the Service Provider ID for Value in this option list
export const getDefaultCarrierOrBrokerOptionsShippingQueue = async ({
  searchString = '',
  showDeleted = true,
}: GetDefaultCarrierOrBrokerOptionsParams) => {
  const { data } = await ServiceProviderApi.serviceProviderWithContactOptions({
    searchTerm: searchString,
    showDeleted,
  });

  const options = data.serviceProvider?.map((carrier: ServiceProviderData) => {
    return {
      label: `${carrier.serviceProviderDescription} - ${
        carrier.serviceProviderType
      } ${carrier.deleted ? '(Deleted)' : ''} (ID: ${
        carrier.serviceProviderId
      })`,
      value: +carrier.serviceProviderId!,
    };
  });

  const serviceLevelOptions = data.p21ServiceLevels?.map(
    (p21ServiceProvider: P21ServiceLevel) => {
      return {
        label: `${p21ServiceProvider.route_description}`,
        value: p21ServiceProvider.route_description,
      };
    }
  );

  return {
    optionList: options,
    serviceProviderOptionList: serviceLevelOptions,
    data,
  };
};

export const getDefaultCarrierOrBrokerOptions = async ({
  searchString = '',
  showDeleted = true,
}: GetDefaultCarrierOrBrokerOptionsParams) => {
  const { data } = await ServiceProviderApi.serviceProviderWithContactOptions({
    searchTerm: searchString,
    showDeleted,
  });

  const options = data.serviceProvider?.map((carrier: ServiceProviderData) => {
    return {
      label: `${carrier.serviceProviderDescription} - ${
        carrier.serviceProviderType
      } ${carrier.deleted ? '(Deleted)' : ''}`,
      value: `${carrier.serviceProviderDescription}`,
    };
  });

  const serviceLevelOptions = data.p21ServiceLevels?.map(
    (p21ServiceProvider: P21ServiceLevel) => {
      return {
        label: `${p21ServiceProvider.route_description}`,
        value: p21ServiceProvider.route_description,
      };
    }
  );

  return {
    optionList: options,
    serviceProviderOptionList: serviceLevelOptions,
    data,
  };
};

interface FieldValueSuggestionData {
  field_name?: string;
  field_value?: string;
  frequency?: number;
}

export const getFieldValueSuggestions = async () => {
  const { data: suggestions } = await OptionsAPI.getOptionsByType(
    optionTypes.fieldValueSuggestion
  );

  return suggestions.map((c: FieldValueSuggestionData) => ({
    type: c.field_name,
    value: c.field_value,
    label: c.field_value,
    frequency: c.frequency,
  }));
};

interface CompanyContactData extends BaseOption {
  contact_id?: number;
  first_name?: string;
  last_name?: string;
}

export const getCompanyContactOptions = async () => {
  const { data: contacts } = await OptionsAPI.getOptionsByType(
    optionTypes.companyContact
  );

  contacts?.forEach((c: CompanyContactData) => {
    const isNameDuplicated = contacts.some(
      (ec: CompanyContactData) =>
        `${ec.first_name}`.toLowerCase() === `${c.first_name}`.toLowerCase() &&
        `${ec.last_name}`.toLowerCase() === `${c.last_name}`.toLowerCase() &&
        ec.contact_id !== c.contact_id
    );
    c.type = optionTypes.companyContact;
    c.value = c.contact_id;
    c.label = `${c.first_name} ${c.last_name}${
      (isNameDuplicated && ` (${c.contact_id})`) || ''
    }`;
  });

  return sortOptions(contacts);
};

interface PriceLibraryData extends BaseOption {
  price_library_id?: string;
  price_library_uid?: string;
  description?: string;
}

export const getPriceLevelsForCustomerPage = async () => {
  const { data: priceLibraries } = await OptionsAPI.getOptionsByType(
    optionTypes.priceLibraries
  );

  priceLibraries?.forEach((p: PriceLibraryData) => {
    p.type = optionTypes.priceLibraries;
    p.value = p.price_library_id;
    p.label = joinString([p.price_library_id, p.description]);
  });

  return priceLibraries;
};

interface InvoiceDeliveryData extends BaseOption {
  invoice_batch_number?: string;
  invoice_batch_desc?: string;
}

export const getInvoiceDeliveryOptions = async () => {
  const { data: invoiceDelivery } = await OptionsAPI.getOptionsByType(
    optionTypes.invoiceDelivery
  );

  invoiceDelivery?.forEach((id: InvoiceDeliveryData) => {
    id.type = optionTypes.invoiceDelivery;
    id.value = id.invoice_batch_number;
    id.label = id.invoice_batch_desc;
  });

  return invoiceDelivery;
};

export const getPriceLibraries = async () => {
  const { data: priceLibraries } = await OptionsAPI.getOptionsByType(
    optionTypes.priceLibraries
  );

  priceLibraries?.forEach((p: PriceLibraryData) => {
    p.type = optionTypes.priceLibraries;
    p.value = p.price_library_uid;
    p.label = joinString([p.price_library_id, p.description]);
  });

  return priceLibraries;
};

interface OpportunityStatusData extends BaseOption {
  data_value?: string;
  display_value?: string;
}

export const getOpportunityStatuses = async () => {
  const { data: opportunityStatuses } = await OptionsAPI.getOptionsByType(
    optionTypes.opportunityStatus
  );
  opportunityStatuses?.forEach((c: OpportunityStatusData) => {
    c.type = optionTypes.opportunityStatus;
    c.value = c.data_value;
    c.label = c.display_value;
  });

  return opportunityStatuses;
};

interface LostSalesOptionData extends BaseOption {
  lost_sales_id?: string;
  lost_sales_desc?: string;
  lost_sales_type?: string;
}

export const getLostSalesOptions = async () => {
  const { data: lostSalesOptions } = await OptionsAPI.getOptionsByType(
    optionTypes.lostSalesOptions
  );
  lostSalesOptions?.forEach((c: LostSalesOptionData) => {
    c.type = optionTypes.lostSalesOptions;
    c.value = c.lost_sales_id;
    c.label = joinString([c.lost_sales_desc, c.lost_sales_type]);
  });

  return lostSalesOptions;
};

interface LeadSourceData extends BaseOption {
  data_value?: string;
  display_value?: string;
}

export const getLeadSources = async () => {
  const { data: leadSources } = await OptionsAPI.getOptionsByType(
    optionTypes.leadSource
  );

  leadSources?.forEach((c: LeadSourceData) => {
    c.type = optionTypes.leadSource;
    c.value = c.data_value;
    c.label = `${c.display_value}`;
  });

  return sortOptions(leadSources);
};

export const getPaymentTerms = async () => {
  const { data: paymentTerms } = await OptionsAPI.getOptionsByType(
    optionTypes.paymentTerm
  );

  return sortOptions(paymentTerms);
};

export const getCompanyInfo = async () => {
  const { data: companyInfo } = await OptionsAPI.getOptionsByType(
    optionTypes.companyInfo
  );

  return companyInfo;
};

interface IndustryData extends BaseOption {
  industry_description?: string;
}

export const getIndustries = async () => {
  const { data: industries } = await OptionsAPI.getPublicOptions(
    optionTypes.industry
  );
  industries?.forEach((c: IndustryData) => {
    c.type = optionTypes.industry;
    c.value = c.industry_description;
    c.label = c.industry_description;
  });

  return sortOptions(industries);
};

interface CustomerTypeData extends BaseOption {
  customer_type_uid?: string;
  customer_type?: string;
}

export const getCustomerTypes = async () => {
  const { data: customerTypes } = await OptionsAPI.getPublicOptions(
    optionTypes.customerType
  );

  customerTypes?.forEach((c: CustomerTypeData) => {
    c.type = optionTypes.customerType;
    c.value = c.customer_type_uid;
    c.label = c.customer_type;
  });

  return sortOptions(customerTypes);
};

interface LeadSourceForCustomersData extends BaseOption {
  source_id?: string;
  source_description?: string;
}

export const getLeadSourcesForCustomerMaintenance = async () => {
  const { data: leadSources } = await OptionsAPI.getOptionsByType(
    optionTypes.leadSourceForCustomers
  );

  leadSources?.forEach((c: LeadSourceForCustomersData) => {
    c.type = optionTypes.leadSourceForCustomers;
    c.value = c.source_id;
    c.label = `${c.source_description}`;
  });

  return sortOptions(leadSources);
};

interface CustomerFlagData extends BaseOption {
  dealer_type_id?: string;
  dealer_type_description?: string;
}

export const getCustomerFlagOptions = async () => {
  const { data: customerFlagOptions } = await OptionsAPI.getPublicOptions(
    optionTypes.customerFlags
  );

  customerFlagOptions?.forEach((c: CustomerFlagData) => {
    c.type = optionTypes.customerFlags;
    c.value = c.dealer_type_id;
    c.label = `${c.dealer_type_description}`;
  });

  return sortOptions(customerFlagOptions);
};

export const getPhotoPriorityOptions = () => {
  const priorityOptions = [
    { value: 'High', label: 'High' },
    { value: 'Normal', label: 'Normal' },
    { value: 'Low', label: 'Low' },
  ];
  return priorityOptions;
};

interface FileLinkGroupData extends BaseOption {
  link_group?: string;
  area_description?: string;
  area_code?: number;
}

export const getFileLinkGroupOptions = async (
  sourceArea: string | string[] | number
) => {
  const { data: options } = await OptionsAPI.getOptionsByType(
    optionTypes.fileLinkGroup
  );

  const filteredOptions = options
    ?.map((c: FileLinkGroupData) => {
      c.type = optionTypes.fileLinkGroup;
      c.value = c.link_group;
      c.label = c.link_group;
      return c;
    })
    .filter((o: FileLinkGroupData) => {
      if (Array.isArray(sourceArea)) {
        return sourceArea.includes(o.area_description!);
      }

      if (typeof sourceArea === 'string') {
        return sourceArea === o.area_description;
      }

      if (typeof sourceArea === 'number') {
        return sourceArea === o.area_code;
      }

      return false;
    });

  return sortOptions(filteredOptions);
};

interface ContactBandData extends BaseOption {
  data_value?: string;
  display_value?: string;
}

export const getContactBands = async () => {
  const { data } = await OptionsAPI.getOptionsByType(optionTypes.contactBand);

  data?.forEach((c: ContactBandData) => {
    c.type = optionTypes.contactBand;
    c.value = c.data_value;
    c.label = c.display_value;
  });

  return sortOptions(data);
};

export const getInternationalStatusOptions = async () => {
  const { data } = await OptionsAPI.getOptionsByType(
    optionTypes.internationalStatus
  );
  data?.forEach((c) => {
    c.type = optionTypes.internationalStatus;
    c.value = c.data_value;
    c.label = c.display_value;
  });

  return sortOptions(data);
};
